
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Layout } from "@/components/Layout"
import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { useAuth } from "@/hooks/useAuth"
import { useUpdateProfile } from "@/hooks/useUserSettings"
import { PushNotificationManager } from "@/components/PushNotificationManager"
import { Loader2, User, Phone, Building, Lock } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PrivacyDataTab } from "@/components/lgpd/PrivacyDataTab"
import { WhatsAppConnectCard } from "@/components/whatsapp/WhatsAppConnectCard"
import { SuperAdminSettings } from "@/components/superadmin/SuperAdminSettings"

const Configuracoes = () => {
  const { profile } = useAuth()
  const whatsappPhone = (profile as (typeof profile & { whatsapp_phone?: string }))?.whatsapp_phone || ""
  const PageLayout = profile?.role === "superadmin" ? SuperAdminLayout : Layout
  const { mutate: updateProfile, isPending } = useUpdateProfile()
  
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || "",
    phone: profile?.phone || "",
    whatsapp_phone: whatsappPhone,
    building: profile?.building || "",
    apartment_number: profile?.apartment_number || ""
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  })

  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateProfile(formData)
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("As senhas não coincidem")
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres")
      return
    }

    setIsChangingPassword(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })

      if (error) {
        console.error("Erro ao alterar senha:", error)
        toast.error("Erro ao alterar senha: " + error.message)
      } else {
        toast.success("Senha alterada com sucesso!")
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        })
      }
    } catch (error) {
      console.error("Erro inesperado:", error)
      toast.error("Erro inesperado ao alterar senha")
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
  }

  if (profile?.role === "superadmin") {
    return <SuperAdminLayout><SuperAdminSettings /></SuperAdminLayout>
  }

  return (
    <PageLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configurações</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerencie suas informações pessoais e segurança</p>
        </div>

        <PushNotificationManager />

        <Tabs defaultValue="perfil">
          <TabsList>
            <TabsTrigger value="perfil">Perfil & Segurança</TabsTrigger>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            <TabsTrigger value="privacidade">Privacidade e Dados (LGPD)</TabsTrigger>
          </TabsList>

          <TabsContent value="perfil">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Informações Pessoais</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nome Completo</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp_phone">WhatsApp</Label>
                  <Input
                    id="whatsapp_phone"
                    value={formData.whatsapp_phone}
                    onChange={(e) => handleInputChange("whatsapp_phone", e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                {(profile?.role === 'morador' || profile?.role === 'sindico') && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="building">Bloco</Label>
                      <Input
                        id="building"
                        value={formData.building}
                        onChange={(e) => handleInputChange("building", e.target.value)}
                        placeholder="A, B, C..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apartment_number">Apartamento</Label>
                      <Input
                        id="apartment_number"
                        value={formData.apartment_number}
                        onChange={(e) => handleInputChange("apartment_number", e.target.value)}
                        placeholder="123"
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" disabled={isPending} className="w-full">
                  {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Salvar Alterações
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lock className="h-5 w-5" />
                  <span>Alterar Senha</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nova Senha</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordInputChange("newPassword", e.target.value)}
                      placeholder="Digite sua nova senha"
                      minLength={6}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordInputChange("confirmPassword", e.target.value)}
                      placeholder="Confirme sua nova senha"
                      minLength={6}
                    />
                  </div>

                  <Button type="submit" disabled={isChangingPassword} className="w-full">
                    {isChangingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Alterar Senha
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Informações da Conta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={profile?.email || ""} disabled />
                </div>
                
                <div className="space-y-2">
                  <Label>Perfil</Label>
                  <Input value={profile?.role || ""} disabled />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Input value={profile?.is_active ? "Ativo" : "Inativo"} disabled />
                </div>
              </CardContent>
            </Card>
          </div>
            </div>
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-4">
            <div className="max-w-2xl">
              <WhatsAppConnectCard />
            </div>
          </TabsContent>

          <TabsContent value="privacidade" className="mt-4">
            <PrivacyDataTab />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  )
}

export default Configuracoes
